import { tagged } from "@mirage/service-logging";
import { throttle } from "lodash";
import { useLayoutEffect, useRef, useState } from "react";

const logger = tagged("omnibox-result-overlay");

// Arbitrary buffer to provide buffer between the viewport and the container
const VIEWPORT_BUFFER = 60;
const MIN_AVAILABLE_HEIGHT = 150;
const THROTTLE_DELAY_MS = 50;

export function useResultsDynamicMaxHeight(
  isTypeaheadOpen: boolean,
  isDualModeLauncher: boolean,
) {
  const listContainerRef = useRef<HTMLUListElement>(null);
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!isTypeaheadOpen || isDualModeLauncher) return;

    // Calculate the available height for the list
    const calculateMaxHeight = throttle(() => {
      if (!listContainerRef.current || !isTypeaheadOpen) return;

      const rect = listContainerRef.current.getBoundingClientRect();
      const availableHeight = window.innerHeight - rect.top - VIEWPORT_BUFFER;

      logger.debug(
        `Calculated available height: ${availableHeight}px with buffer: ${VIEWPORT_BUFFER}px`,
      );

      if (availableHeight >= MIN_AVAILABLE_HEIGHT) {
        setDynamicMaxHeight(availableHeight);
      }
    }, THROTTLE_DELAY_MS);

    calculateMaxHeight();
    window.addEventListener("resize", calculateMaxHeight);
    return () => {
      window.removeEventListener("resize", calculateMaxHeight);
      calculateMaxHeight.cancel();
    };
  }, [isDualModeLauncher, isTypeaheadOpen]);

  return { dynamicMaxHeight, listContainerRef };
}
